<script>
import {emailRegex, urlRegex} from "@/helpers/regex";

export default {
  data() {
    return {
      errors: {
        faqUrl: null,
        footerAboutUrl: null,
        footerContactUrl: null,
        subdomain: null
      },
      originalUrls: null
    };
  },
  computed: {
    urls: {
      get() {
        return this.$store.state?.whitelabel?.urls || {};
      },
      set(value) {
        if (this.$store.state?.whitelabel) {
          this.$store.state.whitelabel.urls = value;
        }
      }
    },
    isModified() {
      return JSON.stringify(this.urls) !== JSON.stringify(this.originalUrls);
    },
    hasValidationErrors() {
      return Object.values(this.errors).some(error => error !== null);
    },
    areRequiredFieldsEmpty() {
      return !!this.urls?.subdomain && this.urls?.subdomain?.length === 0;
    },
    isSaveDisabled() {
      return this.hasValidationErrors || this.areRequiredFieldsEmpty;
    },
    isSubdomainLocked() {
      return this.urls?.subdomain?.length > 0;
    }
  },
  created() {
    if (this.urls?.subdomain?.length) {
      this.originalUrls = JSON.parse(JSON.stringify(this.urls));
    }
  },
  methods: {
    validateSubdomain() {
      const value = this.urls?.subdomain?.trim() || '';
      this.errors.subdomain = value?.length >= 3
        ? null
        : this.$t('validation.subdomain.too.short');
    },
    validateURL(field, allowMailTo = false) {
      const value = this.urls?.[field];

      if (!value) {
        this.errors[field] = null;
        return;
      }

      let isValid = false;

      if (allowMailTo && value.startsWith('mailto:')) {
        const emailPart = value.slice(7);
        isValid = emailRegex.test(emailPart);
      } else {
        isValid = urlRegex.test(value);
      }

      this.errors[field] = isValid ? null : this.$t('validation.url.invalid');
    },
    save() {
      if (!this.isSaveDisabled) {
        this.originalUrls = JSON.parse(JSON.stringify(this.urls));
        this.$store.dispatch('putURLs', this.urls);
      }
    }
  }
};
</script>

<template>
  <v-container fluid class="pt-0">
    <!--    HEADER-->
    <v-row class="pt-0">
      <v-col class="pt-0">
        <h3>{{ $t('component.whitelabel.custom.urls.heading') }}</h3>
      </v-col>
    </v-row>

    <v-divider></v-divider>

    <!-- SUBDOMAIN -->
    <v-row>
      <v-col>
        <div class="d-flex align-center">
          <h4 class="text-subtitle-1 font-weight-bold me-2">
            {{ $t('component.whitelabel.custom.urls.heading.subdomain') }}
          </h4>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-icon
                class="text-info cursor-pointer"
                v-on="on">mdi-information
              </v-icon>
            </template>
            <span>{{ $t('component.whitelabel.custom.urls.subdomain.tooltip') }}</span>
          </v-tooltip>
        </div>
        <v-text-field
          v-if="urls"
          id="whitelabel-subdomain-url"
          v-model="urls.subdomain"
          outlined
          class="mt-3"
          :label="$t('form.label.subdomain')"
          :disabled="isSubdomainLocked"
          :persistent-hint="isSubdomainLocked"
          :hint="isSubdomainLocked ? $t('component.whitelabel.custom.urls.subdomain.locked.tooltip') : $t('component.whitelabel.custom.urls.subdomain.tooltip')"
          :error="!!errors.subdomain"
          :error-messages="errors.subdomain"
          @keyup="validateSubdomain">
        </v-text-field>
      </v-col>
    </v-row>

    <!-- OPTIONAL URLS GROUPED -->
    <v-row>
      <v-col>
        <v-card class="mt-4" outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ $t('component.whitelabel.custom.urls.heading.optional.urls') }}
            <v-tooltip top>
              <template #activator="{ on }">
                <v-icon
                  class="text-info cursor-pointer ms-2"
                  v-on="on">mdi-information
                </v-icon>
              </template>
              <span>{{ $t('component.whitelabel.custom.urls.optional.urls.tooltip') }}</span>
            </v-tooltip>
          </v-card-title>
          <v-card-text>
            <v-text-field
              id="whitelabel-faq-url"
              v-model="urls.faqUrl"
              outlined
              :error-messages="errors.faqUrl"
              :label="$t('form.label.faq')"
              :error="!!errors.faqUrl"
              @keyup="validateURL('faqUrl')">
            </v-text-field>

            <v-text-field
              id="whitelabel-footer-about-url"
              v-model="urls.footerAboutUrl"
              outlined
              :label="$t('form.label.my.company')"
              :error="!!errors.footerAboutUrl"
              :error-messages="errors.footerAboutUrl"
              @keyup="validateURL('footerAboutUrl')">
            </v-text-field>

            <v-text-field
              id="whitelabel-footer-contact-url"
              v-model="urls.footerContactUrl"
              outlined
              :error-messages="errors.footerContactUrl"
              :label="$t('form.label.contact')"
              :hint="$t('component.whitelabel.custom.urls.contact.hint')"
              :error="!!errors.footerContactUrl"
              @keyup="validateURL('footerContactUrl', true)">
            </v-text-field>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- SAVE BUTTON -->
    <v-row>
      <v-col>
        <v-fade-transition>
          <v-btn
            v-if="isModified"
            id="whitelabel-save-urls-button"
            color="primary"
            block
            :disabled="isSaveDisabled"
            @click="save">
            <v-icon class="pe-3">mdi-check</v-icon>
            {{ $t('button.save') }}
          </v-btn>
        </v-fade-transition>
      </v-col>
    </v-row>
  </v-container>

</template>
